import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { formatLocalDate, formatLocalTime } from "utils/dateFormatter";

interface AdvertiserCardProps {
    advertiser: {
        seqId: number;
        name: string;
        managerName: string;
        managerContact: string;
        contractStartDate: string;
        contractEndDate: string;
        status: string;
        created: string;
        rawCrtDt?: string;
    };
    onClick: (advertiser: any) => void;
    onEdit?: (advertiser: any) => void;
    onDelete?: () => void;
    darkMode?: boolean;
}

function AdvertiserCard({ advertiser, onClick, onEdit, onDelete, darkMode }: AdvertiserCardProps) {
    const getStatusColor = (status: string) => {
        switch (status) {
            case "ACTIVE":
                return "success";
            case "PENDING":
                return "warning";
            case "INACTIVE":
                return "error";
            default:
                return "info";
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case "ACTIVE":
                return "활성";
            case "PENDING":
                return "대기";
            case "INACTIVE":
                return "비활성";
            default:
                return status;
        }
    };

    return (
        <Card
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                transition: "all 0.2s ease-in-out",
                cursor: "pointer",
                border: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid rgba(0, 0, 0, 0.12)",
                boxShadow: darkMode
                    ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                    : "0 2px 8px rgba(0, 0, 0, 0.08)",
                "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: darkMode
                        ? "0 8px 20px rgba(255, 255, 255, 0.15)"
                        : "0 8px 20px rgba(0, 0, 0, 0.15)",
                    borderColor: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)",
                },
            }}
            onClick={() => onClick(advertiser)}
        >
            <MDBox p={3}>
                <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <MDTypography variant="h5" fontWeight="bold">
                        {advertiser.name}
                    </MDTypography>
                    <Chip
                        label={getStatusLabel(advertiser.status)}
                        color={getStatusColor(advertiser.status)}
                        size="small"
                        sx={{
                            fontWeight: "medium",
                            ...(advertiser.status === "ACTIVE" && {
                                backgroundColor: "#4CAF50",
                                color: "#ffffff",
                                "& .MuiChip-label": {
                                    color: "#ffffff",
                                },
                            }),
                        }}
                    />
                </MDBox>

                <Divider sx={{ my: 2 }} />

                <MDBox>
                    <MDBox display="flex" alignItems="center" mb={1.5}>
                        <Icon fontSize="small" sx={{ mr: 1.5, color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)" }}>
                            person
                        </Icon>
                        <MDTypography variant="body2" color="text">
                            {advertiser.managerName}
                        </MDTypography>
                    </MDBox>

                    <MDBox display="flex" alignItems="center" mb={1.5}>
                        <Icon fontSize="small" sx={{ mr: 1.5, color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)" }}>
                            phone
                        </Icon>
                        <MDTypography variant="body2" color="text">
                            {advertiser.managerContact}
                        </MDTypography>
                    </MDBox>

                    <MDBox display="flex" alignItems="center" mb={1.5}>
                        <Icon fontSize="small" sx={{ mr: 1.5, color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)" }}>
                            event
                        </Icon>
                        <MDTypography variant="body2" color="text">
                            {advertiser.contractStartDate} ~ {advertiser.contractEndDate}
                        </MDTypography>
                    </MDBox>

                    <MDBox display="flex" alignItems="center" justifyContent="space-between">
                        <MDBox display="flex" alignItems="center">
                            <Icon fontSize="small" sx={{ mr: 1.5, color: darkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)" }}>
                                schedule
                            </Icon>
                            <MDTypography variant="caption" color="text.secondary">
                                등록일:
                                <br />
                                {formatLocalDate(advertiser.rawCrtDt)} {formatLocalTime(advertiser.rawCrtDt)}
                            </MDTypography>
                        </MDBox>
                        {(onEdit || onDelete) && (
                            <MDBox display="flex" gap={0.5}>
                                {onEdit && (
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onEdit(advertiser);
                                        }}
                                        sx={{
                                            color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                            "&:hover": {
                                                backgroundColor: darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.04)",
                                                color: "#49a3f1",
                                            },
                                        }}
                                    >
                                        <Icon fontSize="small">edit</Icon>
                                    </IconButton>
                                )}
                                {onDelete && (
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDelete();
                                        }}
                                        sx={{
                                            color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                            "&:hover": {
                                                backgroundColor: darkMode ? "rgba(255, 100, 100, 0.1)" : "rgba(255, 0, 0, 0.04)",
                                                color: "#f44336",
                                            },
                                        }}
                                    >
                                        <Icon fontSize="small">delete</Icon>
                                    </IconButton>
                                )}
                            </MDBox>
                        )}
                    </MDBox>
                </MDBox>
            </MDBox>
        </Card>
    );
}

export default AdvertiserCard;
